/**
 * repeating-group-dedupe.ts — the SINGLE home for the "one record per section" policy (s149).
 *
 * A record (e.g. a child) can be claimed by only ONE repeating-group section on a form. When the stored
 * answers select the same record in more than one section — a LEGACY double-selection (the picker now
 * prevents new ones with move-semantics) — the FIRST section in declaration order keeps it and every later
 * section drops it. This is the EXACT precedence the fill has always used (buildFillPlan: first-declared
 * binding wins). Extracted here so the fill, the pre-download echo, and (as prevention) the picker all
 * speak ONE rule — no second implementation of the policy.
 *
 * Pure + platform-neutral (no server/client imports) so both sides consume the same function.
 */

export interface GroupSelection {
  /** the section's selection key (e.g. "children_e1"). */
  selectionKey: string;
  /** the selected record rowIds for THIS section, IN FILL ORDER (the record list's own order). */
  rowIds: string[];
}

export interface DedupedGroup {
  selectionKey: string;
  /** rowIds this section keeps (not claimed by an earlier section) — input order preserved. */
  kept: string[];
  /** rowIds dropped because an earlier section already claimed them — input order preserved. */
  dropped: string[];
}

export interface DedupeResult {
  perGroup: DedupedGroup[];
  /** rowId → the selectionKey of the section that KEPT it (the winner). For a dropped rowId, look up its
   *  winner here to name the section it was actually included under. */
  claimedBy: Record<string, string>;
}

/**
 * De-dupe records across ordered sections: the FIRST section (in the given order) to include a rowId keeps
 * it; every later section drops it. Order within a section is preserved. rowIds are globally unique
 * (generic_entries UUIDs), so passing sections of DIFFERENT record types together is harmless — a rowId
 * only ever appears within its own record list's sections.
 */
export function dedupeOrderedGroups(groups: GroupSelection[]): DedupeResult {
  const claimedBy: Record<string, string> = {};
  const perGroup: DedupedGroup[] = groups.map((g) => {
    const kept: string[] = [];
    const dropped: string[] = [];
    for (const id of g.rowIds) {
      if (claimedBy[id] !== undefined) dropped.push(id);
      else { claimedBy[id] = g.selectionKey; kept.push(id); }
    }
    return { selectionKey: g.selectionKey, kept, dropped };
  });
  return { perGroup, claimedBy };
}
